import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail, API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Truck,
  FileText,
  PackageCheck,
  CircleCheck,
  CircleAlert,
  X,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_INFO = {
  gesendet: { label: "Gesendet", cls: "bg-blue-100 text-blue-800 border-blue-300", icon: FileText },
  bestaetigt: { label: "Bestätigt", cls: "bg-cyan-100 text-cyan-800 border-cyan-300", icon: CircleCheck },
  geliefert: { label: "Teilgeliefert", cls: "bg-yellow-100 text-yellow-800 border-yellow-300", icon: PackageCheck },
  eingebucht: { label: "Eingebucht", cls: "bg-green-100 text-green-800 border-green-300", icon: CircleCheck },
  storniert: { label: "Storniert", cls: "bg-zinc-100 text-zinc-600 border-zinc-300", icon: X },
};

const NEXT_ACTIONS = {
  gesendet: ["bestaetigt", "storniert"],
  bestaetigt: ["geliefert", "storniert"],
  geliefert: [],
  eingebucht: [],
  storniert: [],
};

function fmtCur(n) {
  return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(n || 0);
}

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);
  const [receiveDlg, setReceiveDlg] = useState({ open: false, order: null, qtys: {} });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/orders");
      setOrders(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const setStatus = async (oid, status) => {
    try {
      await api.patch(`/orders/${oid}/status`, { status });
      toast.success(`Status: ${STATUS_INFO[status]?.label || status}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const openReceive = (order) => {
    const qtys = {};
    (order.items || []).forEach((it) => {
      const open = Math.max(0, (it.qty || 0) - (it.received_qty || 0));
      qtys[it.article_id] = open;
    });
    setReceiveDlg({ open: true, order, qtys });
  };

  const submitReceive = async () => {
    const items = Object.entries(receiveDlg.qtys)
      .map(([article_id, q]) => ({ article_id, received_qty: Number(q) || 0 }))
      .filter((x) => x.received_qty > 0);
    if (items.length === 0) {
      toast.error("Mindestens eine Position mit Menge eintragen.");
      return;
    }
    try {
      await api.post(`/orders/${receiveDlg.order.id}/receive`, { items });
      toast.success("Wareneingang gebucht. Bestand aktualisiert.");
      setReceiveDlg({ open: false, order: null, qtys: {} });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const downloadPdf = async (oid, orderNo) => {
    const token = localStorage.getItem("lager_token");
    const res = await fetch(`${API}/orders/${oid}/pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      toast.error("PDF konnte nicht geladen werden.");
      return;
    }
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `Bestellung_${orderNo}.pdf`;
    a.click();
  };

  const filtered = orders.filter((o) => {
    if (statusFilter !== "all" && o.status !== statusFilter) return false;
    if (filter) {
      const q = filter.toLowerCase();
      const hay = `${o.order_no} ${o.supplier_name} ${(o.items || []).map((i) => i.name).join(" ")}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const counts = orders.reduce(
    (acc, o) => {
      acc.all++;
      acc[o.status || "gesendet"] = (acc[o.status || "gesendet"] || 0) + 1;
      return acc;
    },
    { all: 0 }
  );

  return (
    <div className="max-w-[1400px] mx-auto space-y-6" data-testid="orders-page">
      <div>
        <div className="mono text-[11px] tracking-[0.25em] text-zinc-500 mb-2">
          BESTELLUNGEN · {orders.length} GESAMT
        </div>
        <h1 className="font-display text-4xl uppercase flex items-center gap-3">
          <Truck className="w-8 h-8" /> Bestellungen
        </h1>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Suche nach Bestell-Nr., Lieferant, Artikel…"
          data-testid="orders-search"
          className="rounded-sm border-zinc-400 h-10 flex-1 min-w-[260px]"
        />
        <div className="flex flex-wrap gap-1">
          {[
            { key: "all", label: "Alle" },
            { key: "gesendet", label: "Gesendet" },
            { key: "bestaetigt", label: "Bestätigt" },
            { key: "geliefert", label: "Teilgeliefert" },
            { key: "eingebucht", label: "Eingebucht" },
            { key: "storniert", label: "Storniert" },
          ].map((b) => (
            <button
              key={b.key}
              onClick={() => setStatusFilter(b.key)}
              data-testid={`orders-filter-${b.key}`}
              className={`px-3 py-1.5 text-xs border rounded-sm transition-colors ${
                statusFilter === b.key
                  ? "bg-black text-white border-black"
                  : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50"
              }`}
            >
              {b.label} {counts[b.key] != null && <span className="opacity-60">· {counts[b.key]}</span>}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center mono text-xs text-zinc-500">LADEN…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-sm text-zinc-500">
            Keine Bestellungen gefunden.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-zinc-900 text-zinc-100">
              <tr>
                <th className="text-left px-4 py-3 mono text-[10px] tracking-[0.2em]">BESTELL-NR.</th>
                <th className="text-left px-4 py-3 mono text-[10px] tracking-[0.2em]">LIEFERANT</th>
                <th className="text-left px-4 py-3 mono text-[10px] tracking-[0.2em]">ARTIKEL</th>
                <th className="text-right px-4 py-3 mono text-[10px] tracking-[0.2em]">SUMME</th>
                <th className="text-left px-4 py-3 mono text-[10px] tracking-[0.2em]">STATUS</th>
                <th className="text-left px-4 py-3 mono text-[10px] tracking-[0.2em]">GESENDET</th>
                <th className="text-right px-4 py-3 mono text-[10px] tracking-[0.2em]">AKTIONEN</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((o) => {
                const status = o.status || "gesendet";
                const info = STATUS_INFO[status] || STATUS_INFO.gesendet;
                const Icon = info.icon;
                const isOpen = expanded === o.id;
                const items = o.items || [];
                const totalQty = items.reduce((a, i) => a + (i.qty || 0), 0);
                const totalRec = items.reduce((a, i) => a + (i.received_qty || 0), 0);
                const totalNet = o.total_net != null
                  ? o.total_net
                  : items.reduce((a, i) => a + (i.qty || 0) * (i.ek_price || 0), 0);
                const canReceive = ["bestaetigt", "geliefert", "gesendet"].includes(status);
                return (
                  <React.Fragment key={o.id}>
                    <tr
                      className="border-t border-zinc-100 hover:bg-zinc-50"
                      data-testid={`order-row-${o.id}`}
                    >
                      <td className="px-4 py-3 mono text-xs font-semibold">{o.order_no}</td>
                      <td className="px-4 py-3">{o.supplier_name || "—"}</td>
                      <td className="px-4 py-3 text-zinc-600">
                        {items.length} Pos. · {totalQty} Stk.
                        {totalRec > 0 && totalRec < totalQty && (
                          <span className="ml-2 text-xs mono text-yellow-700">
                            (eingeg.: {totalRec})
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right mono font-bold">{fmtCur(totalNet)}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 mono text-[10px] tracking-wider border ${info.cls}`}
                        >
                          <Icon className="w-3 h-3" />
                          {info.label.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 py-3 mono text-xs text-zinc-500">
                        {o.sent_at?.slice(0, 16).replace("T", " ")}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-1 flex-wrap">
                          <button
                            onClick={() => setExpanded(isOpen ? null : o.id)}
                            className="p-2 hover:bg-zinc-100 rounded-sm"
                            title="Details"
                            data-testid={`order-toggle-${o.id}`}
                          >
                            {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => downloadPdf(o.id, o.order_no)}
                            className="p-2 hover:bg-zinc-100 rounded-sm"
                            title="PDF"
                            data-testid={`order-pdf-${o.id}`}
                          >
                            <FileText className="w-4 h-4" />
                          </button>
                          {canReceive && (
                            <button
                              onClick={() => openReceive(o)}
                              className="p-2 hover:bg-green-100 hover:text-green-700 rounded-sm"
                              title="Wareneingang buchen"
                              data-testid={`order-receive-${o.id}`}
                            >
                              <PackageCheck className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="border-t border-zinc-100 bg-zinc-50">
                        <td colSpan={7} className="p-4">
                          <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-4">
                            <div>
                              <div className="mono text-[10px] tracking-[0.25em] text-zinc-500 mb-2">
                                POSITIONEN
                              </div>
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-zinc-500 mono">
                                    <th className="text-left pb-1">Artikel</th>
                                    <th className="text-right pb-1">Best.</th>
                                    <th className="text-right pb-1">Erh.</th>
                                    <th className="text-right pb-1">EK</th>
                                    <th className="text-right pb-1">Summe</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {items.map((it, i) => (
                                    <tr key={i} className="border-t border-zinc-200">
                                      <td className="py-1.5">{it.name}</td>
                                      <td className="text-right mono">{it.qty}</td>
                                      <td className="text-right mono">{it.received_qty || 0}</td>
                                      <td className="text-right mono">{fmtCur(it.ek_price)}</td>
                                      <td className="text-right mono font-semibold">
                                        {fmtCur((it.qty || 0) * (it.ek_price || 0))}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>

                              {NEXT_ACTIONS[status]?.length > 0 && (
                                <div className="mt-4">
                                  <div className="mono text-[10px] tracking-[0.25em] text-zinc-500 mb-2">
                                    STATUS ÄNDERN
                                  </div>
                                  <div className="flex gap-2 flex-wrap">
                                    {NEXT_ACTIONS[status].map((next) => (
                                      <Button
                                        key={next}
                                        onClick={() => setStatus(o.id, next)}
                                        variant="outline"
                                        size="sm"
                                        data-testid={`order-status-${o.id}-${next}`}
                                        className="rounded-sm border-zinc-400 h-8"
                                      >
                                        → {STATUS_INFO[next]?.label}
                                      </Button>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                            <div>
                              <div className="mono text-[10px] tracking-[0.25em] text-zinc-500 mb-2">
                                VERLAUF
                              </div>
                              <ol className="space-y-1.5 text-xs">
                                {(o.status_history || []).map((h, i) => (
                                  <li key={i} className="flex items-start gap-2">
                                    <CircleCheck className="w-3 h-3 mt-0.5 text-green-700 shrink-0" />
                                    <div>
                                      <strong>{STATUS_INFO[h.status]?.label || h.status}</strong>
                                      <span className="mono text-zinc-500 ml-1">
                                        {h.at?.slice(0, 16).replace("T", " ")}
                                      </span>
                                      {h.user && <span className="text-zinc-500"> · {h.user}</span>}
                                      {h.note && <div className="text-zinc-600 italic mt-0.5">{h.note}</div>}
                                    </div>
                                  </li>
                                ))}
                              </ol>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <AlertDialog
        open={receiveDlg.open}
        onOpenChange={(o) => !o && setReceiveDlg({ open: false, order: null, qtys: {} })}
      >
        <AlertDialogContent className="rounded-sm max-w-xl" data-testid="receive-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Wareneingang buchen</AlertDialogTitle>
            <AlertDialogDescription>
              Bestell-Nr. <strong>{receiveDlg.order?.order_no}</strong> ·{" "}
              {receiveDlg.order?.supplier_name}. Trage ein, was wirklich angekommen ist — der Bestand wird automatisch erhöht und eine Buchung „Zugang" angelegt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {receiveDlg.order?.items?.map((it) => {
              const open = Math.max(0, (it.qty || 0) - (it.received_qty || 0));
              return (
                <div
                  key={it.article_id}
                  className="grid grid-cols-[1fr_80px_80px] items-center gap-3 bg-zinc-50 border border-zinc-200 rounded-sm p-2"
                >
                  <div className="text-sm">
                    <div className="font-medium">{it.name}</div>
                    <div className="mono text-[11px] text-zinc-500">
                      Bestellt: {it.qty} · Bereits eingegangen: {it.received_qty || 0} · Offen: {open}
                    </div>
                  </div>
                  <Input
                    type="number"
                    min={0}
                    max={open}
                    value={receiveDlg.qtys[it.article_id] ?? 0}
                    onChange={(e) =>
                      setReceiveDlg((s) => ({
                        ...s,
                        qtys: { ...s.qtys, [it.article_id]: e.target.value },
                      }))
                    }
                    data-testid={`receive-qty-${it.article_id}`}
                    className="rounded-sm border-zinc-400 mono text-center"
                  />
                  <div className="mono text-[11px] text-zinc-600 text-right">
                    von {it.qty}
                  </div>
                </div>
              );
            })}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={submitReceive}
              data-testid="receive-submit"
              className="bg-[#F97316] hover:bg-[#EA580C] rounded-sm"
            >
              <PackageCheck className="w-4 h-4 mr-2" /> Wareneingang buchen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
